import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Container, TextField, Button, Typography, Grid, Paper, List, ListItem, ListItemText, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const checkRole = (user, roles) => {
  if (!user || !user.role) return false;
  return roles.includes(user.role);
};

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

function RaceSearchPage() {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [date, setDate] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [message, setMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const response = await axios.get('/api/users/status');
        setCurrentUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user status:', error);
      }
    };
    fetchUserStatus();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!name && !city && !state && !date) {
      setMessage('Please fill in at least one search field.');
      setSearchResults([]);
      return;
    }
    try {
      const params = {};
      if (name) params.name = name;
      if (city) params.city = city;
      if (state) params.state = state;
      if (date) params.date = date;

      const response = await axios.get('/api/races', { params });
      setSearchResults(response.data);
      setMessage('');
    } catch (error) {
      setMessage('Error searching races: ' + (error.response?.data || error.message));
      setSearchResults([]);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Search Races
        </Typography>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={state}
                  label="State"
                  onChange={(e) => setState(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {usStates.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                label="Date"
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth>
                Search
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {message && <Typography color="error" sx={{ mt: 2 }}>{message}</Typography>}

      {searchResults.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Search Results
          </Typography>
          <List>
            {searchResults.map((race) => (
              <ListItem key={race.id} divider>
                <ListItemText
                  primary={
                    currentUser && checkRole(currentUser, ['admin', 'director', 'photographer']) ? (
                      <Link to={`/upload-image/${race.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        {race.name}
                      </Link>
                    ) : (
                      race.name
                    )
                  }
                  secondary={`${race.city}, ${race.state} (${new Date(race.date).toLocaleDateString()})`}
                />
                <Button component={Link} to={`/races/${race.id}/images`} variant="outlined" size="small">
                  View Images
                </Button>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {!message && searchResults.length === 0 && (
        <Typography sx={{ mt: 2 }}>No races found matching your criteria.</Typography>
      )}
    </Container>
  );
}

export default RaceSearchPage;
